import { createContext, useEffect, useRef, useState } from "react";
import useAuth from "../hooks/useAuth";
import masterDataService from "../services/masterData.service";
import usersService from "../services/users.service";
import { MasterData } from "../types/master-data";
import { TinyUser, User } from "../types/user";
import useLocale from "../hooks/useLocale";
import { Dictionary } from "../types/Dictionary";
import { AccessPermissionListSequenceBuilder } from "../utils/stringUtils";
import { axiosWithAuth } from "../utils/customAxios";
import { TelepoDisableConfigProps } from "../types/master-data/TelepoDisableConfig";
import { EntityLocalizationConfiguration } from "../types/locale/Translation";
import localeService from "../services/locale.service";
import { MasterDataLocalizationType } from "../types/master-data/MasterDataLocalizationType";
import customerService from "../services/customer.service";

type MasterDataContextType = {
  data?: MasterData;
  fetchMasterDataKeyTranslation: (
    keyGroup: string,
    key: string,
    defaultValue: string
  ) => string;
  users: TinyUser[];
  standardUsers: User[];
  fetchUpdatedUsers: () => Promise<void>;
  setStandardUsers: React.Dispatch<React.SetStateAction<User[]>>;
  telepoDisableConfig?: TelepoDisableConfigProps;
  fetchUpdatedStandardUsers: () => Promise<void>;
  entityLocalizationNameKeyMapping: EntityLocalizationConfiguration[];
  getEntityNameKeyfromConfigMap: (
    entityName: string,
    fieldName: string
  ) => string;
  isRedCactusActive: boolean;
  setIsRedCactusActive: React.Dispatch<React.SetStateAction<boolean>>;
  fetchCustomerConfigData: () => Promise<void>;
  currentUser: User;
};

const masterDataKeys: string[] = [
  "Platform",
  "License",
  "LicensePackage",
  "AccessPermission",
  "StorageMode",
  "Sentiment",
  "CallStatus",
  "ColorCategory",
  "RecordingNetworkAccessibility",
  "CallDirection",
  "CallType",
  "LocalizationCode",
  "Country",
  "Timezone",
  "CallSubsetType",
  "AuditEntityType",
  "BatchJobType",
  "BatchJobPriority",
  "BatchJobState",
  "EvaluationRange",
  "TopicType",
  "RetentionPeriod",
  "AuditEntityActionType",
  "NotificationType",
  "CrmSystemType",
  "SharedRecordingEntity",
  "TeamsRecordingMode",
  "AudioChannel",
  "AudioCodec",
  "RecordingActionState",
  "TranscriptionEntityType",
  "CallMode",
  "MsTeamsRecordingCallQueueWelcomeTextLanguage"
];

const userData: User = {
  id: 0,
  email: "",
  firstName: "",
  lastName: "",
  loginUserId: 0,
  isMicrosoftLogin: false,
  loginEnabled: false,
  devices: [],
  recordingNetwork: [],
  topics: [],
  moments: [],
  associatedGroups: {
    adminOf: [],
    memberOf: [],
    creatorOf: [],
    playbackNetwork: [],
  },
  isArchived: false,
  scorecards: [],
  callResults: [],
  reasonCodes: []
};

const MasterDataContext = createContext<MasterDataContextType | null>(null);

export const MasterDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useAuth();
  const localeCtx = useLocale();
  const initailMaterDataLocalization: MasterDataLocalizationType = {
    localeCode: localeCtx?.selectedLocale?.current.locale.code ?? "",
    data: localeCtx?.selectedLocale?.current.masterDataTranslations,
  };

  const [data, setData] = useState<MasterData>();
  const [users, setUsers] = useState<TinyUser[]>([]);
  const [entityLocalizationNameKeyMapping, setEntityLocalzationNameKeyMapping] =
    useState<EntityLocalizationConfiguration[]>([]);
  const [standardUsers, setStandardUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [masterDataTranslations, setMasterDataTranslations] = useState<
    MasterDataLocalizationType | undefined
  >(initailMaterDataLocalization);

  const [telepoDisableConfig, setTelepoDisableConfig] = useState<
    TelepoDisableConfigProps | undefined
  >();
  const [isRedCactusActive, setIsRedCactusActive] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User>(userData);

  useEffect(() => {
    let isMounted = true;

    const getMasterData = async () => {
      try {
        isMounted && setIsLoading(true);
        const response = await masterDataService.getMasterData(masterDataKeys);
        mapLocalizationKeyValue(response);
      } catch (err) {
        console.error(err);
      } finally {
        isMounted && setIsLoading(false);
      }
    };

    auth?.accessToken ? getMasterData() : setIsLoading(false);

    return () => {
      isMounted = false;
    };
  }, [auth?.accessToken, masterDataTranslations]);

  useEffect(() => {
    let isMounted = true;

    const fetchAppUsers = async () => {
      try {
        isMounted && setIsLoading(true);
        const response = await usersService.getAllTinyUsers();
        setUsers(response);
      } catch (err) {
        console.error(err);
      } finally {
        isMounted && setIsLoading(false);
      }
    };

    const fetchEntityLocalizationNameKeyMapping = async () => {
      try {
        isMounted && setIsLoading(true);
        const response =
          await localeService.fetchEntityLocalizationNameKeyMapping();
        console.log(response);
        setEntityLocalzationNameKeyMapping(response);
      } catch (err) {
        console.error(err);
      } finally {
        isMounted && setIsLoading(false);
      }
    };

    const fetchStandardUsers = async () => {
      try {
        isMounted && setIsLoading(true);
        const response = await usersService.fetchUsers();
        setStandardUsers(response);
      } catch (err) {
        console.error(err);
      } finally {
        isMounted && setIsLoading(false);
      }
    };

    const fetchTelepoDisableConfig = async () => {
      try {
        isMounted && setIsLoading(true);
        const response = await axiosWithAuth.get(
          "/systemsetting/disableconfig"
        );
        setTelepoDisableConfig(response.data);
      } catch (err) {
        console.error(err);
      } finally {
        isMounted && setIsLoading(false);
      }
    };

    const fetchCurrentUser = async () => {
      try {
        isMounted && setIsLoading(true);
        const response = await usersService.getCurrentUser();
        setCurrentUser(response);
      } catch (err) {
        console.error(err);
      } finally {
        isMounted && setIsLoading(false);
      }
    }

    fetchEntityLocalizationNameKeyMapping();
    auth?.accessToken ? fetchAppUsers() : setIsLoading(false);
    auth?.accessToken ? fetchStandardUsers() : setIsLoading(false);
    auth?.accessToken ? fetchTelepoDisableConfig() : setIsLoading(false);
    auth?.accessToken ? fetchCurrentUser(): setIsLoading(false);

    return () => {
      isMounted = false;
    };
  }, [auth?.accessToken]);

  useEffect(() => {
    let isMounted = true;

    auth?.accessToken &&
    auth.tokenPayload?.CustomerId &&
    standardUsers.length > 0
      ? fetchCustomerConfigData()
      : setIsLoading(false);

    return () => {
      isMounted = false;
    };
  }, [auth?.accessToken, auth?.tokenPayload?.CustomerId, standardUsers]);

  const hasFetchedTranslations = useRef(false);

  useEffect(() => {
    if (
      !hasFetchedTranslations?.current ||
      masterDataTranslations?.localeCode !==
        localeCtx?.selectedLocale?.current?.locale?.code
    ) {
      hasFetchedTranslations.current = true;
      fetchTranslations();
    }
  }, [localeCtx?.selectedLocale]);

  useEffect(() => {
    if (data) {
      mapLocalizationKeyValue({ ...data });
    }
  }, [masterDataTranslations]);

  const mapLocalizationKeyValue = (masterData: MasterData) => {
    if (masterData !== undefined) {
      masterData.auditEntityTypes = masterData.auditEntityTypes.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "AuditEntityType",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.countries = masterData.countries.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "Country",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.timezones = masterData.timezones.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "Timezone",
          ele.localizationKey ?? "",
          ele.displayName
        );
        return ele;
      });
      masterData.platforms = masterData.platforms.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "Platform",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.retentionPeriods = masterData.retentionPeriods.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "RetentionPeriod",
          ele.localizationKey ?? "",
          ele.duration + " " + ele.durationType
        );
        return ele;
      });
      masterData.licensePackages = masterData.licensePackages.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "LicensePackage",
          ele.localizationKey ?? "",
          ele.name
        );
        if (ele.storageDuration) {
          ele.storageDuration.localizationValue = fetchMasterDataKeyTranslation(
            "RetentionPeriod",
            ele.storageDuration?.localizationKey ?? "",
            ele.storageDuration?.duration +
              " " +
              ele.storageDuration?.durationType
          );
        }
        if (ele.baseLicense) {
          ele.baseLicense.localizationValue = fetchMasterDataKeyTranslation(
            "License",
            ele.baseLicense?.localizationKey ?? "",
            ele.baseLicense?.name ?? ""
          );
        }
        return ele;
      });
      masterData.notificationTypes = masterData.notificationTypes.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "NotificationType",
          ele.localizationKey ?? "",
          ele.type
        );
        return ele;
      });
      // masterData.deviceLicenses = masterData.deviceLicenses.map((ele) => {
      //   ele.localizationValue = fetchMasterDataKeyTranslation(
      //     "License",
      //     ele.localizationKey ?? "",
      //     ele.licenseName ?? ""
      //   );
      //   return ele;
      // });
      masterData.accessPermissions = masterData.accessPermissions.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "AccessPermission",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.accessPermissions = AccessPermissionListSequenceBuilder(
        masterData.accessPermissions
      );
      masterData.storageModes = masterData.storageModes.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "StorageMode",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.sentiments = masterData.sentiments.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "Sentiment",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.callStatuses = masterData.callStatuses.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "CallStatus",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.colorCategories = masterData.colorCategories.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "ColorCategory",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.recordingNetworkAccessibilties =
        masterData.recordingNetworkAccessibilties.map((ele) => {
          ele.localizationValue = fetchMasterDataKeyTranslation(
            "RecordingNetworkAccessibility",
            ele.localizationKey ?? "",
            ele.name
          );
          return ele;
        });
      masterData.callDirections = masterData.callDirections.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "CallDirection",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.callTypes = masterData.callTypes.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "CallType",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.callSubsetTypes = masterData.callSubsetTypes.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "CallSubsetType",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.evaluationRange = masterData.evaluationRange.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "EvaluationRange",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.topicType = masterData.topicType.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "TopicType",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.auditEntityActionType = masterData.auditEntityActionType.map(
        (ele) => {
          ele.localizationValue = fetchMasterDataKeyTranslation(
            "AuditEntityActionType",
            ele.localizationKey ?? "",
            ele.name
          );
          return ele;
        }
      );
      masterData.sharedRecordingEntities =
        masterData.sharedRecordingEntities.map((ele) => {
          ele.localizationValue = fetchMasterDataKeyTranslation(
            "SharedRecordingEntity",
            ele.localizationKey ?? "",
            ele.name
          );
          return ele;
        });
      masterData.recordingActionState = masterData.recordingActionState.map(
        (ele) => {
          ele.localizationValue = fetchMasterDataKeyTranslation(
            "RecordingActionState",
            ele.localizationKey ?? "",
            ele.name
          );
          return ele;
        }
      );
      masterData.transcriptionEntityTypes =
        masterData.transcriptionEntityTypes.map((ele) => {
          ele.localizationValue = fetchMasterDataKeyTranslation(
            "TranscriptionEntityType",
            ele.localizationKey ?? "",
            ele.name
          );
          return ele;
        });
      masterData.callModes = masterData.callModes.map((ele) => {
        ele.localizationValue = fetchMasterDataKeyTranslation(
          "CallMode",
          ele.localizationKey ?? "",
          ele.name
        );
        return ele;
      });
      masterData.teamsRecordingMode = masterData.teamsRecordingMode.map(
        (ele) => {
          ele.localizationValue = fetchMasterDataKeyTranslation(
            "TeamsRecordingMode",
            ele.localizationKey ?? "",
            ele.modeName
          );
          return ele;
        }
      );

      setData(masterData);
    }
  };

  const fetchTranslations = async () => {
    try {
      const resp = await localeCtx?.setMasterDataTranslation(
        masterDataKeys,
        "MasterData"
      );
      const dataTranslation: MasterDataLocalizationType = {
        localeCode: localeCtx?.selectedLocale?.current.locale.code ?? "",
        data: resp,
      };
      setMasterDataTranslations(dataTranslation);
    } catch (err) {
      console.error(err);
      const dataTranslation: MasterDataLocalizationType = {
        localeCode: localeCtx?.selectedLocale?.previous.locale.code ?? "",
        data: localeCtx?.selectedLocale?.previous.masterDataTranslations,
      };
      setMasterDataTranslations(dataTranslation);
    } finally {
    }
  };

  const fetchMasterDataKeyTranslation = (
    keyGroup: string,
    key: string,
    defaultValue: string
  ): string => {
    return masterDataTranslations?.data?.[keyGroup]?.[key] ?? defaultValue;
  };

  const fetchUpdatedUsers = async () => {
    try {
      const response = await usersService.getAllTinyUsers();
      setUsers(response);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUpdatedStandardUsers = async () => {
    try {
      const response = await usersService.fetchUsers();
      setStandardUsers(response);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCustomerConfigData = async () => {
    try {
      const findUser: User | undefined = standardUsers.find(
        (_u: User) => _u.id === auth?.user?.id
      );
      const response = await customerService.getCustomerConfiguration(
        auth?.tokenPayload?.CustomerId ?? 0
      );
      setIsRedCactusActive(
        findUser &&
          (findUser.redCactusEnabled || response.redCactusEnabledAllUsers) &&
          response.redCactusEnabled
          ? true
          : false
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getEntityNameKeyfromConfigMap = (
    entityName: string,
    fieldName: string
  ): string => {
    const ele = entityLocalizationNameKeyMapping.find(
      (x) =>
        x.entityName.toLowerCase().trim() === entityName.toLowerCase().trim() &&
        x.fieldName.toLowerCase().trim() === fieldName.toLowerCase().trim()
    );

    return ele?.localizationKey ?? "";
  };

  return (
    <MasterDataContext.Provider
      value={{
        fetchUpdatedUsers,
        data,
        users,
        standardUsers,
        fetchMasterDataKeyTranslation,
        setStandardUsers,
        telepoDisableConfig,
        fetchUpdatedStandardUsers,
        entityLocalizationNameKeyMapping,
        getEntityNameKeyfromConfigMap,
        isRedCactusActive,
        setIsRedCactusActive,
        fetchCustomerConfigData,
        currentUser
      }}
    >
      {children}
    </MasterDataContext.Provider>
  );
};

export default MasterDataContext;
